import axios from 'axios';

/**
 * Resolve the API base URL with a runtime guard.
 *
 * Why: a Create-React-App bundle bakes `process.env.REACT_APP_BACKEND_URL`
 * at build time. When the same build is later served behind a *different*
 * domain (custom domain, Cloudflare, mirror, etc.) the baked URL becomes a
 * cross-origin call. With `withCredentials: true` browsers then either:
 *   (a) block the request entirely for CORS+credentials reasons, or
 *   (b) drop the `session_token` cookie because the origins differ.
 * The auth flow silently breaks even though the server is reachable.
 *
 * Strategy: if the env URL points to a different origin than the page we're
 * loaded from, prefer the page origin — the platform always serves `/api`
 * on the same host. Local dev (localhost) keeps using the explicit env URL
 * because there the dev server is at :3000 and the API at :8001 (separate
 * origins by design).
 */
const ENV_BACKEND = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

function resolveBackendUrl() {
  if (typeof window === 'undefined' || !window.location) return ENV_BACKEND;
  const origin = window.location.origin;
  // localhost dev: trust the env value (frontend on :3000, backend elsewhere).
  if (origin.startsWith('http://localhost') || origin.startsWith('http://127.0.0.1')) {
    return ENV_BACKEND;
  }
  // No env override → same-origin.
  if (!ENV_BACKEND) return origin;
  // Env URL matches current origin → keep it as configured.
  try {
    const envOrigin = new URL(ENV_BACKEND).origin;
    if (envOrigin === origin) return ENV_BACKEND;
  } catch {
    // Malformed env URL — fall back to current origin.
    return origin;
  }
  // Env URL differs from current origin (typical for custom-domain deploys
  // where the build was made against the platform default host). Prefer
  // same-origin so cookies/CORS just work.
  console.warn(
    `[api] REACT_APP_BACKEND_URL (${ENV_BACKEND}) differs from current origin (${origin}); ` +
    `using same-origin to keep auth cookies working.`
  );
  return origin;
}

const BACKEND_URL = resolveBackendUrl();
export { BACKEND_URL };

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
});

// Routes that must remain accessible without a session (no redirect on 401).
const PUBLIC_PATHS = ['/login', '/changelog', '/roadmap'];

// Etichette dei campi per i messaggi di validazione (422 di FastAPI/Pydantic).
const FIELD_LABELS = {
  module_no: 'N° modulo', key_actions: 'Azioni chiave', other_type: 'Specifica "Altro"',
  objectives: 'Obiettivi', next_check_date: 'Prossima verifica', hours: 'Ore',
  notes: 'Note', topics: 'Argomenti trattati', modules_covered: 'Moduli trattati',
  tutor_name: 'Nome tutor', period: 'Periodo', level: 'Livello', date: 'Data',
  display_name: 'Nome', name: 'Nome', email: 'Email', types: 'Tipologia',
};

function ruleText(e) {
  const ctx = e.ctx || {};
  switch (e.type) {
    case 'string_too_long': return `massimo ${ctx.max_length} caratteri`;
    case 'too_long': return `massimo ${ctx.max_length} elementi`;
    case 'string_too_short': return `minimo ${ctx.min_length} caratteri`;
    case 'missing': return 'campo obbligatorio';
    case 'less_than_equal': return `valore massimo ${ctx.le}`;
    case 'greater_than_equal': return `valore minimo ${ctx.ge}`;
    default: return e.msg || 'valore non valido';
  }
}

// Pydantic ritorna `detail` come array di oggetti: passato a un toast non
// renderizza NULLA (l'utente crede che il salvataggio sia andato a buon fine).
// Lo normalizziamo in una stringa leggibile.
function detailToMessage(detail) {
  if (!Array.isArray(detail)) return detail;
  const parts = detail.slice(0, 3).map(e => {
    const loc = (e.loc || []).filter(x => typeof x === 'string' && x !== 'body');
    const field = loc.length ? FIELD_LABELS[loc[loc.length - 1]] || loc[loc.length - 1] : null;
    return field ? `${field}: ${ruleText(e)}` : ruleText(e);
  });
  return `Dati non validi — ${parts.join(' · ')}`;
}

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const onPublicPath = PUBLIC_PATHS.some(p => window.location.pathname.startsWith(p));
    if (err.response?.status === 401 && !onPublicPath) {
      window.location.href = '/login';
    }
    if (err.response?.data?.detail) {
      err.response.data.detail = detailToMessage(err.response.data.detail);
    }
    return Promise.reject(err);
  }
);

export default api;

export const authApi = {
  session: (session_id) => api.post('/auth/session', { session_id }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
};

export const brandsApi = {
  list: () => api.get('/brands'),
  create: (data) => api.post('/brands', data),
  update: (id, data) => api.put(`/brands/${id}`, data),
  remove: (id) => api.delete(`/brands/${id}`),
};

export const areaManagersApi = {
  list: () => api.get('/area-managers'),
  create: (data) => api.post('/area-managers', data),
  update: (id, data) => api.put(`/area-managers/${id}`, data),
  setStores: (id, storeIds) => api.put(`/area-managers/${id}/stores`, { store_ids: storeIds }),
};

export const areasApi = {
  list: () => api.get('/areas'),
  create: (data) => api.post('/areas', data),
  update: (id, data) => api.put(`/areas/${id}`, data),
  delete: (id) => api.delete(`/areas/${id}`),
};

export const kitsApi = {
  list: () => api.get('/inventory/kits'),
  create: (data) => api.post('/inventory/kits', data),
  update: (kitId, data) => api.put(`/inventory/kits/${kitId}`, data),
  remove: (kitId) => api.delete(`/inventory/kits/${kitId}`),
  assign: (kitId, data) => api.post(`/inventory/kits/${kitId}/assign`, data),
  seedBase: () => api.post('/inventory/kits/seed-base'),
  baseTemplate: () => api.get('/inventory/kits/base-template'),
  compose: (data) => api.post('/inventory/kits/compose', data),
};

export const stationsApi = {
  list: (storeId) => api.get(`/stores/${storeId}/stations`),
  create: (storeId, data) => api.post(`/stores/${storeId}/stations`, data),
  remove: (stationId) => api.delete(`/stations/${stationId}`),
  assignItem: (stationId, itemId) => api.post(`/stations/${stationId}/items`, { item_id: itemId }),
  unassignItem: (stationId, itemId) => api.delete(`/stations/${stationId}/items/${itemId}`),
};

export const storesApi = {
  list: (params) => api.get('/stores', { params }),  get: (id) => api.get(`/stores/${id}`),
  create: (data) => api.post('/stores', data),
  update: (id, data) => api.put(`/stores/${id}`, data),
};

export const storeMappingsApi = {
  list: () => api.get('/store-mappings'),
  create: (data) => api.post('/store-mappings', data),
  update: (id, data) => api.put(`/store-mappings/${id}`, data),
};

export const importsApi = {
  list: () => api.get('/imports'),
  get: (id) => api.get(`/imports/${id}`),
  upload: (formData) => api.post('/imports/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirm: (id, data) => api.post(`/imports/${id}/confirm`, data),
  delete: (id) => api.delete(`/imports/${id}`),
  uploadBudget: (formData) => api.post('/imports/budget/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirmBudget: (id, data) => api.post(`/imports/budget/${id}/confirm`, data),
  uploadCosts: (formData) => api.post('/imports/costs/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  confirmCosts: (id, data) => api.post(`/imports/costs/${id}/confirm`, data),
  // Daily import (one-day Fresha export → kpi_values_daily)
  previewDaily: (formData) => api.post('/imports/daily/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  uploadDaily: (formData) => api.post('/imports/daily', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Chrome extension binary (zip), OPS-only
  downloadExtension: () => api.get('/imports/extension/download', { responseType: 'arraybuffer' }),
};

export const kpiApi = {
  definitions: () => api.get('/kpi/definitions'),
  thresholds: () => api.get('/kpi/thresholds'),
  values: (params) => api.get('/kpi/values', { params }),
  setValue: (data) => api.post('/kpi/values', data),
  updateDefinition: (id, data) => api.put(`/kpi/definitions/${id}`, data),
  updateThreshold: (id, data) => api.put(`/kpi/thresholds/${id}`, data),
};

export const healthScoreConfigApi = {
  get: () => api.get('/health-score/config'),
  update: (data) => api.put('/health-score/config', data),
  reset: () => api.post('/health-score/config/reset'),
};

export const dashboardApi = {
  store: (storeId, params) => api.get(`/dashboard/store/${storeId}`, { params }),
  area: (params) => api.get('/dashboard/area', { params }),
  overview: (params) => api.get('/dashboard/overview', { params }),
};

export const budgetsApi = {
  list: (params) => api.get('/budgets', { params }),
  getStoreYear: (storeId, year) => api.get(`/budgets/store/${storeId}/year/${year}`),
  save: (data) => api.post('/budgets', data),
  getSetup: () => api.get('/budget-setup'),
  updateSetup: (data) => api.put('/budget-setup', data),
  healSources: () => api.post('/budgets/heal-sources'),
};

export const costsApi = {
  categories: () => api.get('/cost-categories'),
  createCategory: (data) => api.post('/cost-categories', data),
  list: (params) => api.get('/store-costs', { params }),
  create: (data) => api.post('/store-costs', data),
  update: (id, data) => api.put(`/store-costs/${id}`, data),
  delete: (id) => api.delete(`/store-costs/${id}`),
};

export const usersApi = {
  list: () => api.get('/users'),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  invite: (data) => api.post('/users/invite', data),
  resendInvite: (userId) => api.post(`/users/${userId}/resend-invite`),
};

export const trainingApi = {
  meta: () => api.get('/training/meta'),
  listRegisters: (params) => api.get('/training/registers', { params }),
  getRegister: (id) => api.get(`/training/registers/${id}`),
  createRegister: (data) => api.post('/training/registers', data),
  updateRegister: (id, data) => api.put(`/training/registers/${id}`, data),
  deleteRegister: (id) => api.delete(`/training/registers/${id}`),
  addDailyLog: (id, data) => api.post(`/training/registers/${id}/daily-log`, data),
  deleteDailyLog: (id, entryId) => api.delete(`/training/registers/${id}/daily-log/${entryId}`),
  signRegister: (id, data) => api.post(`/training/registers/${id}/sign`, data),
  registerPdf: (id, withReport) => api.get(`/training/registers/${id}/pdf`, { params: withReport ? { with_report: true } : {}, responseType: 'blob' }),
  archiveRegister: (id, data) => api.post(`/training/registers/${id}/archive`, data),
  getSettings: () => api.get('/training/settings'),
  updateSettings: (data) => api.put('/training/settings', data),
  videoInit: (data) => api.post('/training/videos/init', data),
  videoChunk: (uploadId, blob) => api.post(`/training/videos/${uploadId}/chunk`, blob,
    { headers: { 'Content-Type': 'application/octet-stream' }, timeout: 180000 }),
  videoComplete: (uploadId) => api.post(`/training/videos/${uploadId}/complete`, null, { timeout: 600000 }),
  videoTicket: (fileId) => api.post(`/training/videos/${fileId}/ticket`),
  listTemplates: () => api.get('/training/templates'),
  createTemplate: (data) => api.post('/training/templates', data),
  getTemplate: (id) => api.get(`/training/templates/${id}`),
  updateTemplate: (id, data) => api.put(`/training/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/training/templates/${id}`),
  listEvaluations: (params) => api.get('/training/evaluations', { params }),
  getEvaluation: (id) => api.get(`/training/evaluations/${id}`),
  createEvaluation: (data) => api.post('/training/evaluations', data),
  updateEvaluation: (id, data) => api.put(`/training/evaluations/${id}`, data),
  finalizeEvaluation: (id) => api.post(`/training/evaluations/${id}/finalize`),
  signEvaluation: (id, data) => api.post(`/training/evaluations/${id}/sign`, data),
  deleteEvaluation: (id) => api.delete(`/training/evaluations/${id}`),
  evaluationPdf: (id, withReport) => api.get(`/training/evaluations/${id}/pdf`, { params: withReport ? { with_report: true } : {}, responseType: 'blob' }),
  archiveEvaluation: (id) => api.post(`/training/evaluations/${id}/archive`),
  files: (kind) => api.get('/training/files', { params: kind ? { kind } : {} }),
  uploadFile: (formData) => api.post('/training/files', formData),
  downloadFile: (id) => api.get(`/training/files/${id}/download`, { responseType: 'blob' }),
  deleteFile: (id) => api.delete(`/training/files/${id}`),
  unlocks: (staffId) => api.get('/training/unlocks', { params: { staff_id: staffId } }),
  staffSummary: (staffId) => api.get(`/training/summary/${staffId}`),
  unlock: (data) => api.post('/training/unlocks', data),
  removeUnlock: (staffId, fileId) => api.delete(`/training/unlocks/${staffId}/${fileId}`),
};

export const complianceApi = {
  template: () => api.get('/compliance/template'),
  updateTemplate: (data) => api.put('/compliance/template', data),
  checks: (storeId) => api.get('/compliance/checks', { params: { store_id: storeId } }),
  createCheck: (data) => api.post('/compliance/checks', data),
  check: (id) => api.get(`/compliance/checks/${id}`),
  updateCheck: (id, data) => api.put(`/compliance/checks/${id}`, data),
  finalizeCheck: (id) => api.post(`/compliance/checks/${id}/finalize`),
  signCheck: (id, data) => api.post(`/compliance/checks/${id}/sign`, data),
  addCheckComment: (id, data) => api.post(`/compliance/checks/${id}/comments`, data),
  deleteCheck: (id) => api.delete(`/compliance/checks/${id}`),
  checkPdf: (id, withReport) => api.get(`/compliance/checks/${id}/pdf`,
    { params: withReport ? { with_report: true } : {}, responseType: 'blob', timeout: 120000 }),
};

export const auditApi = {
  list: (params) => api.get('/audit-logs', { params }),
};

export const dataHealthApi = {
  get: (params) => api.get('/data-health', { params }),
};

export const overridesApi = {
  list: (params) => api.get('/manual-overrides', { params }),
};

export const storeNotesApi = {
  list: (storeId) => api.get(`/stores/${storeId}/notes`),
  create: (storeId, text, opts = {}) =>
    api.post(`/stores/${storeId}/notes`, { text, ...opts }),
  update: (storeId, noteId, text) => api.put(`/stores/${storeId}/notes/${noteId}`, { text }),
  remove: (storeId, noteId) => api.delete(`/stores/${storeId}/notes/${noteId}`),
};

export const inventoryApi = {
  categories: () => api.get('/inventory/categories'),
  createCategory: (data) => api.post('/inventory/categories', data),
  updateCategory: (id, data) => api.put(`/inventory/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/inventory/categories/${id}`),
  listItems: (params) => api.get('/inventory/items', { params }),
  stats: () => api.get('/inventory/stats'),
  getItem: (id) => api.get(`/inventory/items/${id}`),
  createItem: (data) => api.post('/inventory/items', data),
  updateItem: (id, data) => api.put(`/inventory/items/${id}`, data),
  deleteItem: (id) => api.delete(`/inventory/items/${id}`),
  addEvent: (id, data) => api.post(`/inventory/items/${id}/events`, data),
  deleteEvent: (id, eventId) => api.delete(`/inventory/items/${id}/events/${eventId}`),
  bulkReassign: (payload) => api.post('/inventory/items/bulk-reassign', payload),
  bulkDelete: (itemIds) => api.post('/inventory/items/bulk-delete', { item_ids: itemIds }),
  removeFromKit: (itemId) => api.post(`/inventory/items/${itemId}/remove-from-kit`),
  storeDashboard: (storeId) => api.get(`/inventory/store-dashboard/${storeId}`),
  listAssignments: (id) => api.get(`/inventory/items/${id}/assignments`),
  // Lista combinata staff+manager per popolare il dropdown "Affidato a"
  listAssignees: () => api.get('/inventory/assignees'),
  eventAttachmentUrl: (eventId, attId) =>
    `${api.defaults.baseURL}/inventory/events/${eventId}/attachments/${attId}/download`,
  // Foto native sull'oggetto (P1 idea_ae79a708845d)
  listAttachments: (id) => api.get(`/inventory/items/${id}/attachments`),
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/inventory/items/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  attachmentUrl: (id, attId) => `${api.defaults.baseURL}/inventory/items/${id}/attachments/${attId}/download`,
  deleteAttachment: (id, attId) => api.delete(`/inventory/items/${id}/attachments/${attId}`),
  labelsPdf: (itemIds) => api.post('/inventory/labels/pdf',
    { item_ids: itemIds }, { responseType: 'blob' }),
};

export const aiApi = {
  storeInsights: (storeId, { month, year, quarter, annual, force = false, cache_only = false }) => {
    const params = { year, force, cache_only };
    if (annual) params.annual = true;
    else if (quarter) params.quarter = quarter;
    else params.month = month;
    return api.get(`/ai/insights/store/${storeId}`, { params });
  },
  areaInsights: ({ month, year, quarter, annual, area_manager_id, brand_id, area, store_ids, force = false, cache_only = false }) => {
    const params = { year, force, cache_only };
    if (annual) params.annual = true;
    else if (quarter) params.quarter = quarter;
    else params.month = month;
    if (area_manager_id && area_manager_id !== 'all') params.area_manager_id = area_manager_id;
    if (brand_id && brand_id !== 'all') params.brand_id = brand_id;
    if (area && area !== 'all') params.area = area;
    if (store_ids && store_ids.length) params.store_ids = store_ids.join(',');
    return api.get('/ai/insights/area', { params });
  },
  saveAction: (scope, entityId, negativeIndex, { signature, text }) =>
    api.put(`/ai/insights/${scope}/${entityId}/actions/${negativeIndex}`, { signature, text }),
  executiveSummary: ({ month, year, quarter, cache_only, force }) =>
    api.get('/ai/executive-summary', { params: { month, year, quarter, cache_only, force } }),
  downloadStorePdf: (storeId, { month, year, quarter, annual }) => {
    const params = { year };
    if (annual) params.annual = true;
    else if (quarter) params.quarter = quarter;
    else params.month = month;
    return api.get(`/ai/insights/pdf/store/${storeId}`, { params, responseType: 'blob' });
  },
  downloadAreaPdf: ({ month, year, quarter, annual, area_manager_id, brand_id, area, store_ids }) => {
    const params = { year };
    if (annual) params.annual = true;
    else if (quarter) params.quarter = quarter;
    else params.month = month;
    if (area_manager_id && area_manager_id !== 'all') params.area_manager_id = area_manager_id;
    if (brand_id && brand_id !== 'all') params.brand_id = brand_id;
    if (area && area !== 'all') params.area = area;
    if (store_ids && store_ids.length) params.store_ids = store_ids.join(',');
    return api.get('/ai/insights/pdf/area', { params, responseType: 'blob' });
  },
};

export const healthApi = {
  status: () => api.get('/health'),
  ready: () => api.get('/health/ready'),
};

export const backlogApi = {
  list: (params) => api.get('/backlog', { params }),
  create: (data) => api.post('/backlog', data),
  update: (id, data) => api.put(`/backlog/${id}`, data),
  remove: (id) => api.delete(`/backlog/${id}`),
};

export const roadmapApi = {
  // Public — no auth required
  list: (voterId) => api.get('/roadmap', { params: voterId ? { voter_id: voterId } : {} }),
  // value: 1 = upvote (toggle), -1 = downvote (toggle), 0 = clear esplicito
  vote: (id, voterId, value = 1) => api.post(`/roadmap/${id}/vote`, { voter_id: voterId, value }),
  submit: (data) => api.post('/roadmap', data),
  // Commenti pubblici sulle idee — anonimi con name/email opzionali
  listComments: (id) => api.get(`/roadmap/${id}/comments`),
  addComment: (id, data) => api.post(`/roadmap/${id}/comments`, data),
  // Moderazione (OPS only)
  deleteComment: (ideaId, commentId) => api.delete(`/backlog/${ideaId}/comments/${commentId}`),
};

export const reportsApi = {
  previewWeeklyGaps: () => api.get('/reports/weekly-gaps/preview'),
  sendWeeklyGaps: () => api.post('/reports/weekly-gaps/send'),
};

export const adminApi = {
  hardReset: (confirmation) => api.post('/admin/hard-reset', { confirmation }),
  rbacAudit: () => api.get('/admin/rbac-audit'),
};

export const brandDailyConfigApi = {
  list: () => api.get('/settings/brand-daily-config'),
  get: (brandId) => api.get(`/settings/brand-daily-config/${brandId}`),
  upsert: (brandId, payload) => api.put(`/settings/brand-daily-config/${brandId}`, payload),
};

// Google Reviews (idea utente 2026-05-15) — fetch reviews + rating per store
// dalla Places API (New). Cache TTL 1h sul backend, refresh manuale OPS-only.
export const googleReviewsApi = {
  store: (storeId) => api.get(`/stores/${storeId}/google-reviews`),
  refreshStore: (storeId) => api.post(`/admin/google-reviews/refresh/${storeId}`),
  refreshAll: () => api.post('/admin/google-reviews/refresh'),
  health: (withLive = false) => api.get('/admin/google-reviews/health', { params: { with_live: withLive } }),
  realignStore: (storeId) => api.post(`/admin/google-reviews/realign/${storeId}`),
};

export const staffApi = {
  list: (params) => api.get('/staff', { params }),
  get: (id) => api.get(`/staff/${id}`),
  me: () => api.get('/staff/me'),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  remove: (id) => api.delete(`/staff/${id}`),
  // Cartelle staff (globali, gerarchiche, OPS/HR only per write)
  listFolders: () => api.get('/staff-folders'),
  createFolder: (data) => api.post('/staff-folders', data),
  renameFolder: (id, data) => api.put(`/staff-folders/${id}`, data),
  deleteFolder: (id) => api.delete(`/staff-folders/${id}`),
  // Documenti
  listDocs: (staffId, params) => api.get(`/staff/${staffId}/documents`, { params }),
  uploadDoc: (staffId, formData) => api.post(`/staff/${staffId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  moveDoc: (staffId, docId, folderId) => api.patch(`/staff/${staffId}/documents/${docId}`, { folder_id: folderId }),
  bulkPayslips: (formData) => api.post('/staff-payslips/bulk', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  downloadDocUrl: (staffId, docId) => `/staff/${staffId}/documents/${docId}/download`,
  deleteDoc: (staffId, docId) => api.delete(`/staff/${staffId}/documents/${docId}`),
  // Firma documenti (2026-06-25)
  requestSignature: (staffId, docId, payload = {}) => api.post(`/staff/${staffId}/documents/${docId}/request-signature`, payload),
  signDoc: (staffId, docId, payload) => api.post(`/staff/${staffId}/documents/${docId}/sign`, payload),
  listMyPendingSignatures: () => api.get('/staff/me/pending-signatures'),
  // Achievements
  listAch: (staffId) => api.get(`/staff/${staffId}/achievements`),
  createAch: (staffId, data) => api.post(`/staff/${staffId}/achievements`, data),
  deleteAch: (staffId, achId) => api.delete(`/staff/${staffId}/achievements/${achId}`),
  // SesameHR import
  importSesamePreview: (formData) => api.post('/staff/import-sesame/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importSesameCommit: (data) => api.post('/staff/import-sesame/commit', data),
  // Fresha performance import
  importFreshaPreview: (formData) => api.post('/staff/import-fresha/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importFreshaCommit: (data) => api.post('/staff/import-fresha/commit', data),
  // Performance read
  getPerformance: (staffId, params) => api.get(`/staff/${staffId}/performance`, { params }),
  // Home Staff aggregato (greeting + perf + ticket + docs + agenda + ach)
  myDashboard: () => api.get('/staff/me/dashboard'),
  // Inventario affidato a questo staff member (refactor 2026-05-29)
  getInventory: (staffId) => api.get(`/staff/${staffId}/inventory`),
  // Monthly HR Report (PDF) — preview + trigger + runs
  monthlyReportPreviewUrl: () => '/staff/monthly-report/preview',
  monthlyReportTrigger: () => api.post('/staff/monthly-report/trigger'),
  monthlyReportRuns: () => api.get('/staff/monthly-report/runs'),
  // Fresha Alias Management (2026-07-01)
  listFreshaAliases: () => api.get('/fresha-aliases'),
  listFreshaUnmatched: () => api.get('/fresha-unmatched'),
  freshaUnmatchedCount: () => api.get('/fresha-unmatched/count'),
  listFreshaIgnored: () => api.get('/fresha-unmatched/ignored'),
  ignoreFreshaUnmatched: (freshaName) => api.post('/fresha-unmatched/ignore', { fresha_name: freshaName }),
  unignoreFreshaUnmatched: (freshaName) => api.post('/fresha-unmatched/unignore', { fresha_name: freshaName }),
  createFreshaAlias: (data) => api.post('/fresha-aliases', data),
  updateFreshaAlias: (id, data) => api.put(`/fresha-aliases/${id}`, data),
  deleteFreshaAlias: (id) => api.delete(`/fresha-aliases/${id}`),
};

export const ticketsApi = {
  stats: () => api.get('/tickets/stats'),
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  changeStatus: (id, data) => api.post(`/tickets/${id}/status`, data),
  resolveItem: (id, itemId, data) => api.post(`/tickets/${id}/items/${itemId}/resolve`, data),
  addComment: (id, text) => api.post(`/tickets/${id}/comments`, { text }),
  deleteComment: (id, commentId) => api.delete(`/tickets/${id}/comments/${commentId}`),
  remove: (id) => api.delete(`/tickets/${id}`),
  // Allegati foto (Fase 2 Assistenza)
  listAttachments: (id) => api.get(`/tickets/${id}/attachments`),
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/tickets/${id}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  attachmentUrl: (id, attId) => `${api.defaults.baseURL}/tickets/${id}/attachments/${attId}/download`,
  deleteAttachment: (id, attId) => api.delete(`/tickets/${id}/attachments/${attId}`),
};





export const leaveApi = {
  list: (params) => api.get('/leave-requests', { params }),
  create: (data) => api.post('/leave-requests', data),
  decide: (id, decision, reason) => api.patch(`/leave-requests/${id}/decide`, { decision, reason }),
  cancel: (id) => api.delete(`/leave-requests/${id}`),
  pendingCount: () => api.get('/leave-requests/pending-count'),
  balance: (staffId, year) => api.get(`/staff/${staffId}/leave-balance`, { params: { year } }),
  setBalance: (staffId, data) => api.put(`/staff/${staffId}/leave-balance`, data),
};

export const personalNotesApi = {
  getNote: () => api.get('/personal-notes'),
  saveNote: (text) => api.put('/personal-notes', { text }),
  listTasks: (params) => api.get('/personal-tasks', { params }),
  createTask: (data) => api.post('/personal-tasks', data),
  updateTask: (id, data) => api.put(`/personal-tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/personal-tasks/${id}`),
  uploadAttachment: (taskId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/personal-tasks/${taskId}/attachments`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  attachmentUrl: (taskId, attId) => `${api.defaults.baseURL}/personal-tasks/${taskId}/attachments/${attId}/download`,
  deleteAttachment: (taskId, attId) => api.delete(`/personal-tasks/${taskId}/attachments/${attId}`),
};

export const notificationsApi = {
  list: (params) => api.get('/notifications', { params }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Push (Web Push API + VAPID).
// Vedi /app/frontend/src/lib/usePushNotifications.js per il flusso completo.
export const pushApi = {
  publicKey: () => api.get('/push/public-key'),
  status: () => api.get('/push/status'),
  subscribe: (payload) => api.post('/push/subscribe', payload),
  unsubscribe: (payload) => api.post('/push/unsubscribe', payload),
  test: () => api.post('/push/test'),
};

